import React from 'react';
import { Box, Card, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import type { OsFamily, PackageId } from './deployAgentTypes';

interface OsPackageSelectorProps {
  os: OsFamily | null;
  packageId: PackageId | null;
  onOsChange: (os: OsFamily) => void;
  onPackageChange: (packageId: PackageId) => void;
}

const OS_CONFIG: {
  id: OsFamily;
  label: string;
  icon: React.ReactNode;
  packages: { id: PackageId; label: string }[];
}[] = [
    {
      id: 'linux',
      label: 'LINUX',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C9.5 2 8 4 8 6C8 7.5 8.5 8.5 9 9.5C7.5 10.5 6 12.5 6 15C6 18.5 8.5 21 12 21C15.5 21 18 18.5 18 15C18 12.5 16.5 10.5 15 9.5C15.5 8.5 16 7.5 16 6C16 4 14.5 2 12 2Z" fill="#2D3748" />
          <circle cx="10.5" cy="7" r="0.8" fill="white" />
          <circle cx="13.5" cy="7" r="0.8" fill="white" />
          <path d="M12 17C10.5 17 9.5 16.5 9 16" stroke="white" strokeWidth="1" strokeLinecap="round" />
        </svg>
      ),
      packages: [
        { id: 'linux-debian', label: 'Linux Debian' },
        { id: 'linux-rhel', label: 'Linux RHEL' },
      ],
    },
    {
      id: 'windows',
      label: 'WINDOWS',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 5.5L10 4.5V11H3V5.5ZM11 4.3L21 3V11H11V4.3ZM3 12H10V18.5L3 17.5V12ZM11 12H21V20L11 18.7V12Z" fill="#2D3748" />
        </svg>
      ),
      packages: [{ id: 'windows', label: 'Windows' }],
    },
    {
      id: 'macos',
      label: 'macOS',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.3 7.3C16.9 7.8 16.2 8.2 15.5 8.2C14.8 8.2 14.2 7.8 13.7 7.4C13 6.8 12.4 6.8 11.7 7.4C11.2 7.8 10.6 8.2 9.9 8.2C9.2 8.2 8.5 7.8 8.1 7.3C7.4 6.5 7.3 5.2 7.8 4.2C8.3 3.2 9.4 2.5 10.4 2.5C11.2 2.5 11.8 2.9 12.4 2.9C13 2.9 13.6 2.5 14.4 2.5C15.5 2.5 16.4 3.3 16.9 4.2C16 4.7 15.4 5.8 15.5 6.9C15.6 8 16.4 8.9 17.3 9.2C17.2 9.8 17 10.4 16.7 11C16.1 12.2 15.2 13.5 14.1 13.5C13.4 13.5 13 13.1 12.3 13.1C11.6 13.1 11.2 13.5 10.5 13.5C9.4 13.5 8.5 12.3 7.9 11.1C7 9.2 7.4 6.5 9.2 5.4C10 4.9 10.9 4.8 11.7 5.3C12.4 5.7 13.1 5.8 13.8 5.4C14.5 5 15.2 4.9 15.9 5.2C16.3 5.4 16.6 5.6 16.9 5.9C16.7 6.3 16.6 6.8 16.6 7.3C16.6 7.3 16.6 7.3 17.3 7.3Z" fill="#2D3748" />
        </svg>
      ),
      packages: [
        { id: 'macos', label: 'Macos' },
      ],
    },
  ];

const OsPackageSelector: React.FC<OsPackageSelectorProps> = ({
  os,
  packageId,
  onOsChange,
  onPackageChange,
}) => {
  const handlePackageSelect = (osId: OsFamily, pkgId: PackageId) => {
    if (os !== osId) {
      onOsChange(osId);
    }
    onPackageChange(pkgId);
  };

  return (
    <Box display="flex" gap={2} flexWrap="wrap">
      {OS_CONFIG.map((osItem) => {
        const isSelected = os === osItem.id;
        return (
          <Card
            key={osItem.id}
            variant="outlined"
            onClick={() => onOsChange(osItem.id)}
            style={{
              flex: '1 1 280px',
              minWidth: 260,
              border: isSelected ? '1px solid #3182CE' : '1px solid #E2E8F0',
              borderRadius: 8,
              backgroundColor: isSelected ? '#F7FAFC' : '#FFFFFF',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <Box p={2}>
              {/* Header: icon + OS name */}
              <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1.5}>
                {osItem.icon}
                <Typography
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: '#2D3748',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {osItem.label}
                </Typography>
              </Box>

              {/* Divider */}
              <Box style={{ height: 1, backgroundColor: '#E2E8F0', marginBottom: 12 }} />

              {/* Package radios */}
              <RadioGroup
                value={isSelected ? packageId : ''}
                onChange={(e) => handlePackageSelect(osItem.id, e.target.value as PackageId)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: osItem.packages.length > 2 ? '1fr 1fr' : '1fr',
                  gap: '4px 12px',
                  justifyItems: osItem.packages.length === 1 ? 'center' : 'start',
                }}
              >
                {osItem.packages.map((pkg) => (
                  <FormControlLabel
                    key={pkg.id}
                    value={pkg.id}
                    control={<Radio size="small" color="primary" />}
                    label={
                      <Typography style={{ fontSize: 13, color: '#4A5568' }}>
                        {pkg.label}
                      </Typography>
                    }
                    style={{ margin: 0, pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ))}
              </RadioGroup>
            </Box>
          </Card>
        );
      })}
    </Box>
  );
};

export default OsPackageSelector;